import React, { useEffect, useState } from 'react';
import { Layout } from '../../Layout/Layout';
import SettingsItem from './SettingsItem';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { useNavigate } from 'react-router-dom';
import Loader from '../../Loader/Loader';
import { selectUserId, selectUserPermissions } from '../../../containers/Login/LoginSlice';
import { Page } from '../../Page/Page';
import BigFloatButton from '../../BigFloatButton/BigFloatButton';
import { fetchUserSettings } from './settingsSlice/actionCreators';
import { updateUserSettings } from '../../../api/users';
import { selectIsSettingsLoading, selectSettings } from './settingsSlice';
import type { SettingsType } from './settingsSlice/types';
import _ from 'lodash';
import { SFlexDiv } from './Settings.styles';
import { selectTenant } from '../../../containers/Organizations/OrganizationsSlice';
import {
  refreshUser,
  refreshUserPermissions,
} from '../../../containers/Login/LoginSlice/actionCreators';

function Settings() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userPermissions = useAppSelector(selectUserPermissions);
  const userId = useAppSelector(selectUserId);
  const tenant = useAppSelector(selectTenant);
  const settings = useAppSelector(selectSettings);
  const isLoading = useAppSelector(selectIsSettingsLoading);

  const [settingsState, setSettingsState] = useState<SettingsType>({
    notifyChecklists: false,
    notifyDocuments: false,
    notifyWithEmail: false,
  });
  const [settingsStateInitial, setSettingsStateInitial] = useState<SettingsType>({
    notifyChecklists: false,
    notifyDocuments: false,
    notifyWithEmail: false,
  });

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserSettings(userId));
      dispatch(refreshUser());
      dispatch(refreshUserPermissions());
    }
  }, []);

  useEffect(() => {
    if (settings) {
      setSettingsState(settings);
      setSettingsStateInitial(settings);
    }
  }, [settings]);

  const notifyWithEmailCallback = (notifyWithEmail: boolean) => {
    setSettingsState({ ...settingsState, notifyWithEmail: !notifyWithEmail });
  };

  const notifyChecklistsCallback = (notifyChecklists: boolean) => {
    setSettingsState({ ...settingsState, notifyChecklists: !notifyChecklists });
  };

  const notifyDocumentsCallback = (notifyDocuments: boolean) => {
    setSettingsState({
      ...settingsState,
      notifyDocuments: !notifyDocuments,
    });
  };

  const showChecklistSetting = userPermissions?.checklists.mayUse ?? false;
  const showDocumentSetting = userPermissions?.documents.mayRead ?? false;

  const settingsItems = [
    {
      visible: true,
      id: 0,
      name: 'sidebar_settings_notifyByEmail',
      checked: settingsState.notifyWithEmail,
      callback: notifyWithEmailCallback,
    },
    {
      visible: showChecklistSetting,
      id: 2,
      name: 'sidebar_settings_allowNews',
      checked: settingsState.notifyChecklists,
      callback: notifyChecklistsCallback,
    },
    {
      visible: showDocumentSetting,
      id: 3,
      name: 'sidebar_settings_allowDocsNotifications',
      checked: settingsState.notifyDocuments,
      callback: notifyDocumentsCallback,
    },
  ];

  const save = async () => {
    try {
      if (!userId || !tenant) {
        throw new Error('Cannot save settings when tenant or user id is missing');
      }
      const res = await updateUserSettings({
        userId,
        tenantId: tenant.id,
        body: {
          receiveEmails: settingsState.notifyWithEmail,
          notifications: {
            checklists: settingsState.notifyChecklists,
            documents: settingsState.notifyDocuments,
          },
        },
      });
      if (res.error) {
        return;
      }
      navigate('/sidebar');
    } catch (error) {
      console.log('error log ', error);
    }
  };

  if (isLoading) {
    return (
      <Layout isMessageLayout message="sidebar_settings_header" to="/sidebar">
        <Page>
          <Loader />
        </Page>
      </Layout>
    );
  }

  return (
    <Layout isMessageLayout message="sidebar_settings_header" to="/sidebar" showBottomTabs>
      <Page>
        {settingsItems.map((item, key) => (
          <SettingsItem
            visible={item.visible}
            name={item.name}
            id={item.id}
            checked={item.checked}
            key={key}
            callback={item.callback}
          />
        ))}
        <SFlexDiv />
        {!_.isEqual(settingsStateInitial, settingsState) && (
          <BigFloatButton tx="save" onClick={save} />
        )}
      </Page>
    </Layout>
  );
}

export default Settings;
